import { isBefore, subMonths } from 'date-fns'
import { useEffect, useState } from 'react'
import { useAuth } from '../modules/auth'
import { handleGetRequest } from '../services'

// How often the org admin is asked to confirm their contact info.
export const CONTACT_REVIEW_INTERVAL_MONTHS = 6

// Set once per browser session so we evaluate (and fetch the org) at most once
// per session, regardless of route changes / re-renders.
const SESSION_FLAG = 'contactInfoReviewEvaluated'

export interface OrgContactInfo {
  _id: string
  org_name?: string
  daglig_leder?: string
  email?: string
  mobile_phone?: string
  invoice_email?: string
  home_page?: string
  address_line1?: string | null
  post_code?: string
  city?: string
  // Backend field — ISO timestamp of the last time an admin confirmed the
  // contact info. Null/absent means "never confirmed".
  contact_info_last_confirmed_at?: string | null
}

function isReviewDue(org: OrgContactInfo): boolean {
  const confirmedAt = org.contact_info_last_confirmed_at
  if (!confirmedAt) return true
  const parsed = new Date(confirmedAt)
  if (Number.isNaN(parsed.getTime())) return true
  return isBefore(parsed, subMonths(new Date(), CONTACT_REVIEW_INTERVAL_MONTHS))
}

/**
 * Decides whether the org admin should be prompted to review their contact
 * info (once every {@link CONTACT_REVIEW_INTERVAL_MONTHS} months) and provides
 * the current org details to pre-fill the form.
 *
 * Suppressed for non-admins and while the org is still onboarding (Vipps
 * pending or the intro tour not yet completed) so the prompt never stacks on
 * top of the Vipps modal / reactour.
 */
export function useContactInfoReview() {
  const { currentUser } = useAuth()
  const [org, setOrg] = useState<OrgContactInfo | null>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    const user = currentUser?.user
    const orgId = user?.organisation_id?._id
    const isAdmin = user?.user_type === 'admin'

    if (!isAdmin || !orgId) return undefined
    if (sessionStorage.getItem(SESSION_FLAG)) return undefined

    // Don't interrupt onboarding: the Vipps modal / intro tour own the screen
    // until the org is set up and the tour has been completed once.
    const onboarding = user?.organisation_id?.msn_status === 'pending' || !user?.tour_step
    if (onboarding) return undefined

    let cancelled = false
    const evaluate = async () => {
      // Pass a no-op setLoading so we don't trigger the global loading overlay.
      const response = await handleGetRequest(`/organisation/${orgId}`)(() => {})
      if (cancelled) return
      sessionStorage.setItem(SESSION_FLAG, '1')
      const data = response?.data as OrgContactInfo | undefined
      if (!data) return
      setOrg(data)
      if (isReviewDue(data)) setShow(true)
    }
    evaluate()

    return () => {
      cancelled = true
    }
  }, [currentUser])

  return { show, org, hide: () => setShow(false) }
}
